import { ChangeEvent, useCallback, useMemo } from 'react';

import { AgentPortalFeatureCode } from '@wallet-manager/pfh-pmp-node-def-types';
import {
  EnumDistributorAgentProgramStatus,
  EnumRebateMode
} from '@wallet-manager/pfh-pmp-node-def-types/dist/src/DbModel/SwapAgent/';

import { SingleSelection } from '../../../../components';
import AgentProgramNameSelection from '../../../../components/AgentProgramNameSelection';
import DaIdAutoCompleteFilter from '../../../../components/DaIdAutoCompleteFilter';
import { useDatePicker } from '../../../../components/DatePicker';
import FilterTemplate from '../../../../components/Layout/FilterTemplate';
import { SelectChangeEvent } from '../../../../components/MuiGenerals';
import { useTranslation } from '../../../../hooks';
import {
  useFilterFields,
  useGenFilterBtnBar,
  useGenFilterFields
} from '../../../../hooks/useFilter';
import { Iprefix } from '../../../../hooks/useTranslation';
import { useZusDialogStore } from '../../../../zustand/store';
import { IsearchParams, useZusParams } from '../hooks';
import {
  assignProgramDialogTranslateKey as AssignProgramTK,
  filterTableTranslateKey as TK
} from '../mappings/translate';

const initFields = {
  distributorAgentId: '',
  programName: '',
  rebateMode: '',
  status: '',
};

export default function Filter() {
  const translatePrefix: Iprefix = 'distributorAgentProgram';

  const { t, tc } = useTranslation(translatePrefix);
  const zusDialog = useZusDialogStore();

  const assignProgramBtnName = t(AssignProgramTK.assignProgramBtn);
  const handleClickingAssignProgramBtn = () => zusDialog.open('createRequestDialog');
  const assignProgramPermission =
    AgentPortalFeatureCode.Setting.DistributorAgentProgram.AssignProgram;

  const { filterBtnBar } = useGenFilterBtnBar([
    {
      name: assignProgramBtnName,
      clickHandler: handleClickingAssignProgramBtn,
      permission: assignProgramPermission,
    },
  ]);

  const DateFields = { CreationDate: useDatePicker(), LastModifiedDate: useDatePicker() };

  const { onReset, fields, setFields } = useFilterFields({
    initFields,
    dateFields: DateFields,
  });

  const zusParams = useZusParams();

  const apiParams: IsearchParams = useMemo(() => {
    const { ...rest } = fields;
    const { start: createdDateFrom, end: createdDateTo } = DateFields.CreationDate;
    const { start: lastModifiedDateFrom, end: lastModifiedDateTo } = DateFields.LastModifiedDate;

    return {
      ...rest,
      createdDateFrom,
      createdDateTo,
      lastModifiedDateFrom,
      lastModifiedDateTo,
    };
  }, [
    fields,
    DateFields.CreationDate.start,
    DateFields.CreationDate.end,
    DateFields.LastModifiedDate.start,
    DateFields.LastModifiedDate.end,
  ]);

  const onSearch = useCallback(() => {
    zusParams.setBody({ ...apiParams, page: 0 });
    zusParams.refetch();
  }, [apiParams]);

  const onChange =
    (field: keyof typeof fields) =>
      (e: SelectChangeEvent<string> | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFields((f) => ({ ...f, [field]: e.target.value }));
      };
  const onClearSelect = (field: keyof Omit<typeof fields, 'distributorAgentId'>) => () => {
    setFields((f) => ({ ...f, [field]: '' }));
  };

  const { filterFields } = useGenFilterFields(
    [
      { label: t(TK.creationTime), element: <DateFields.CreationDate.Picker type={'dateTime'} /> },
      {
        label: t(TK.lastModifiedTime),
        element: <DateFields.LastModifiedDate.Picker type={'dateTime'} />,
      },
      {
        label: t(TK.distributorAgentId),
        element: (
          <DaIdAutoCompleteFilter
            value={fields.distributorAgentId}
            onChange={(value) => setFields((f) => ({ ...f, distributorAgentId: value }))}
            onClear={() => setFields((f) => ({ ...f, distributorAgentId: '' }))}
          />
        ),
      },
      {
        label: t(TK.programName),
        element: (
          <AgentProgramNameSelection
            mode={'all'}
            value={fields.programName}
            onChange={onChange('programName')}
            clearSelect={onClearSelect('programName')}
          />
        ),
      },
      {
        label: t(TK.rebateMode),
        element: (
          <SingleSelection
            label={tc('phSelection', { fieldName: t(TK.rebateMode) })}
            value={fields.rebateMode}
            onChange={onChange('rebateMode')}
            enumData={EnumRebateMode}
            clearSelect={onClearSelect('rebateMode')}
          />
        ),
      },
      {
        label: t(TK.status),
        element: (
          <SingleSelection
            label={tc('phSelection', { fieldName: t(TK.status) })}
            value={fields.status}
            onChange={onChange('status')}
            enumData={EnumDistributorAgentProgramStatus}
            clearSelect={onClearSelect('status')}
          />
        ),
      },
    ],
    [fields, DateFields]
  );

  return (
    <FilterTemplate
      filterBtnBar={filterBtnBar}
      filterFields={filterFields}
      onSearch={onSearch}
      onReset={onReset}
    />
  );
}
